import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { CuiToastComponent } from './toast.component';

/** Options for a transient application notification. */
export interface CuiToastOptions {
  variant?: 'error' | 'info';
  durationMs?: number;
}

/** Displays one transient notification at a time. */
@Injectable({ providedIn: 'root' })
export class CuiToastService {
  /** Services. */
  private readonly overlay = inject(Overlay);

  /** State. */
  private current: OverlayRef | undefined;
  private timeout: ReturnType<typeof setTimeout> | undefined;

  /** Shows a message at the top of the viewport. */
  show(message: string, options: CuiToastOptions = {}): void {
    this.dismiss();
    const position = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .top('var(--cui-spacing-24)');
    const overlayRef = this.overlay.create({ positionStrategy: position });
    const component = overlayRef.attach(new ComponentPortal(CuiToastComponent));
    component.instance.message.set(message);
    component.instance.variant.set(options.variant ?? 'info');
    component.instance.dismiss.subscribe(() => this.dismiss());
    this.current = overlayRef;
    this.timeout = setTimeout(() => this.dismiss(), options.durationMs ?? 5000);
  }

  /** Hides the current notification. */
  dismiss(): void {
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = undefined;
    this.current?.dispose();
    this.current = undefined;
  }
}
